import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, withModifiers as _withModifiers, withKeys as _withKeys, resolveDirective as _resolveDirective, withDirectives as _withDirectives, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "flex flex-column gap-1" }
const _hoisted_2 = {
  key: 0,
  class: "flex justify-content-end py-3"
}
const _hoisted_3 = {
  key: 1,
  class: "flex flex-column align-items-center justify-content-center p-5 gap-3 surface-card border-1 surface-border border-round"
}
const _hoisted_4 = {
  key: 2,
  class: "border-round border-none"
}
const _hoisted_5 = {
  key: 0,
  class: "info-alert"
}
const _hoisted_6 = {
  key: 1,
  class: "flex flex-column align-items-center gap-3 text-500 text-center py-4"
}
const _hoisted_7 = {
  key: 2,
  class: "text-center text-500 py-4"
}
const _hoisted_8 = { key: 0 }
const _hoisted_9 = { key: 3 }
const _hoisted_10 = {
  key: 0,
  class: "section-header"
}
const _hoisted_11 = { class: "section-title" }
const _hoisted_12 = { class: "grid" }
const _hoisted_13 = {
  key: 0,
  class: "col-12"
}
const _hoisted_14 = { class: "flex align-items-center justify-content-between gap-3" }
const _hoisted_15 = ["disabled", "aria-label", "aria-pressed", "onClick"]
const _hoisted_16 = { class: "flex align-items-center flex-wrap gap-2 mt-2" }
const _hoisted_17 = {
  key: 1,
  class: "card-title-text"
}
const _hoisted_18 = { class: "text-500 text-sm line-height-3" }
const _hoisted_19 = { class: "flex align-items-center justify-content-between gap-3 mt-auto pt-2" }
const _hoisted_20 = { class: "flex align-items-center gap-2" }

import GlobalHeader from "@/components/GlobalHeader.vue";
import { rotaInicialDoPerfil } from "@/utils/rotaInicialDoPerfil";
import PeriodoDialog from "@/components/defesa/PeriodoDialog.vue";
import TrabalhoEmUsoDialog from "@/components/trabalhos/TrabalhoEmUsoDialog.vue";
import { useConferirTrabalhoEmUso } from "@/composables/trabalhos/useConferirTrabalhoEmUso";
import { usePermissions } from "@/composables/usePermissions";
import trabalhoService from "@/services/trabalhos/trabalhoService";
import { useAuthStore } from "@/stores/AuthStore";
import { useCursoStore } from "@/stores/CursoStore";
import Button from "primevue/button";
import Card from "primevue/card";
import InputText from "primevue/inputtext";
import ProgressSpinner from "primevue/progressspinner";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";

type Work = {
  id: string;
  title: string;
  is_oficial: boolean;
  is_favorite: boolean;
  curso_id?: number | null;
  curso_codigo?: string;
  createdAt: string;
  updatedAt: string;
  defesa_status?: string | null;
  data_inicio?: string | null;
  data_fim?: string | null;
  status_prazo?: string | null;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'DashboardView',
  setup(__props) {

const searchQuery = ref("");
const loadingWorks = ref(true);
const criandoTrabalho = ref(false);
const works = ref<Work[]>([]);
const isComponentMounted = ref(false);

const editingWorkId = ref<string | null>(null);
const editingTitle = ref("");
const savingTitle = ref(false);
const savingFavoriteIds = ref<Set<string>>(new Set());
const periodoDialogVisible = ref(false);
const periodoDialogWork = ref<Work | null>(null);
// Refs e variáveis reativas

const router = useRouter();
const authStore = useAuthStore();
const cursoStore = useCursoStore();
const trabalhoEmUso = useConferirTrabalhoEmUso(() => authStore.user?.id);
const { can } = usePermissions();
const confirm = useConfirm();
const toast = useToast();

const cursoAtual = computed(() => cursoStore.cursoAtual);

const pageTitle = computed(() => {
  if (authStore.isCoordenador || authStore.isSecretaria || authStore.isCoordenadorTcc) {
    return 'Gerenciar trabalhos';
  }
  return 'Meus Projetos';
});

const pageDescription = computed(() => {
  if (authStore.isCoordenador || authStore.isSecretaria || authStore.isCoordenadorTcc) {
    return 'Gerencie os trabalhos de conclusão de curso dos alunos';
  }
  return 'Gerencie seus trabalhos e crie novos projetos';
});

const filteredWorks = computed(() =>
  works.value.filter((w) =>
    w.title.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
);

const isFinalized = (w: Work) => w.defesa_status?.startsWith('CONCLUIDA_') ?? false;
const activeWorks = computed(() => filteredWorks.value.filter((w) => !isFinalized(w)));
const finalizedWorks = computed(() => filteredWorks.value.filter((w) => isFinalized(w)));
const favoriteWorks = computed(() => activeWorks.value.filter((w) => w.is_favorite));

const workSections = computed(() => {
  const sections: { key: string; title: string; items: Work[]; showTitle: boolean }[] = [];
  const hasFinalized = finalizedWorks.value.length > 0;

  if (activeWorks.value.length > 0) {
    sections.push({ key: "regular", title: "Projetos", items: activeWorks.value, showTitle: true });
    sections.push({ key: "favorites", title: "Favoritos", items: favoriteWorks.value, showTitle: true });
  }

  if (hasFinalized) {
    sections.push({ key: "finalized", title: "Finalizados", items: finalizedWorks.value, showTitle: true });
  }

  if (sections.length === 0 && filteredWorks.value.length > 0) {
    sections.push({ key: "regular", title: "", items: filteredWorks.value, showTitle: false });
  }

  return sections;
});

const loadTrabalhos = async () => {
  if (!authStore.isAuthenticated || !isComponentMounted.value) {
    works.value = [];
    loadingWorks.value = false;
    return;
  }

  try {
    loadingWorks.value = true;

    // Define params: apenas inclui curso_id se houver curso selecionado
    const params: { curso_id?: number } = {};
    if (cursoAtual.value?.id) {
      params.curso_id = cursoAtual.value.id;
    }

    const { data } = await trabalhoService.getTrabalhos(params);

    works.value = (Array.isArray(data) ? data : []).map(
      (t: {
        id: number | string;
        titulo?: string;
        is_oficial?: boolean;
        curso_id?: number | null;
        curso_codigo?: string;
        is_favorite?: boolean;
        favorito?: boolean;
        created_at?: string;
        updated_at?: string;
        created_at_formatted?: string;
        updated_at_formatted?: string;
        defesa?: { id: number; status: string } | null;
      }) => ({
        id: String(t.id),
        title: String(t.titulo || "Sem título"),
        is_oficial: Boolean(t.is_oficial),
        is_favorite: Boolean(t.is_favorite ?? t.favorito ?? false),
        curso_id: t.curso_id || null,
        createdAt: String(t.created_at_formatted || t.created_at || ""),
        updatedAt: String(t.updated_at_formatted || t.updated_at || ""),
        defesa_status: t.defesa?.status || null,
        data_inicio: (t as any).data_inicio || null,
        data_fim: (t as any).data_fim || null,
        status_prazo: (t as any).status_prazo || null,
      })
    );
  } catch (err) {
    console.error("❌ Falha ao carregar trabalhos", err);
    works.value = [];
  } finally {
    loadingWorks.value = false;
  }
};

watch(() => cursoAtual.value?.id, async (newId, oldId) => {
  if (!authStore.isAuthenticated || !isComponentMounted.value) {
    return;
  }

  if (newId !== oldId) {
    await loadTrabalhos();
  }
}, { immediate: false });

onMounted(async () => {
  isComponentMounted.value = true;

  const destino = rotaInicialDoPerfil(authStore.activeRole || authStore.getDefaultRole());
  if (destino !== '/projetos') {
    router.push(destino);
    return;
  }

  if ((authStore.isAluno || authStore.isCoordenador || authStore.isSecretaria || authStore.isCoordenadorTcc || authStore.isOrientador) && !cursoStore.initialized) {
    await cursoStore.initializeCurso();
  }

  await loadTrabalhos();
});

onBeforeUnmount(() => {
  isComponentMounted.value = false;
});

function slugify(text: string) {
  return text
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
}

function openEditor(work: Work) {
  const slug = slugify(work.title);
  void trabalhoEmUso.abrirConferindo(Number(work.id), () => {
    router.push({
      name: "editor",
      params: { id: `${work.id}-${slug}` },
    });
  });
}

function podeExcluirTrabalho(work: Work): boolean {
  if (!can('trabalho.delete')) {
    return false;
  }

  if (work.is_oficial) {
    return false;
  }

  return true;
}

function confirmDelete(work: Work) {
  confirm.require({
    message: `Excluir o trabalho "${work.title}"? Esta ação não pode ser desfeita.`,
    header: "Confirmar Exclusão",
    icon: "pi pi-exclamation-triangle",
    acceptLabel: "Excluir",
    rejectLabel: "Cancelar",
    acceptClass: "p-button-danger",
    accept: async () => {
      try {
        await trabalhoService.deleteTrabalho(Number(work.id));
        works.value = works.value.filter((w) => w.id !== work.id);
        toast.add({
          severity: "success",
          summary: "Excluído",
          detail: "Trabalho removido",
          life: 3000,
        });
      } catch (e) {
        toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Falha ao excluir trabalho",
          life: 4000,
        });
      } finally {
        confirm.close();
      }
    },
  });
}

async function criarNovoTrabalho() {
  try {
    criandoTrabalho.value = true;

    const payload: { titulo?: string; curso_id?: number } = {};

    if (cursoAtual.value?.id) {
      payload.curso_id = cursoAtual.value.id;
    }

    const { data } = await trabalhoService.createTrabalho(payload);

    toast.add({
      severity: "success",
      summary: "Trabalho Criado",
      detail: "Trabalho criado com sucesso!",
      life: 3000,
    });

    router.push({
      name: "editor",
      params: { id: data.id }
    });
  } catch (err: any) {
    const errorMsg = err?.response?.data?.message || "Erro ao criar trabalho";
    toast.add({
      severity: "error",
      summary: "Erro",
      detail: errorMsg,
      life: 5000,
    });
  } finally {
    criandoTrabalho.value = false;
  }
}

function startEdit(work: Work) {
  editingWorkId.value = work.id;
  editingTitle.value = work.title;
}

function cancelEdit() {
  editingWorkId.value = null;
  editingTitle.value = "";
}

async function saveTitle(work: Work) {
  const newTitle = editingTitle.value.trim();

  if (!newTitle) {
    toast.add({
      severity: "warn",
      summary: "Aviso",
      detail: "O título não pode estar vazio",
      life: 3000,
    });
    return;
  }

  if (newTitle === work.title) {
    cancelEdit();
    return;
  }

  try {
    savingTitle.value = true;
    await trabalhoService.updateTrabalho(Number(work.id), { titulo: newTitle });

    // Atualiza o título na lista local
    const workIndex = works.value.findIndex(w => w.id === work.id);
    if (workIndex !== -1) {
      works.value[workIndex].title = newTitle;
    }

    toast.add({
      severity: "success",
      summary: "Título atualizado",
      detail: "O título foi alterado com sucesso",
      life: 3000,
    });

    cancelEdit();
  } catch (err: any) {
    const errorMsg = err?.response?.data?.message || "Erro ao atualizar título";
    toast.add({
      severity: "error",
      summary: "Erro",
      detail: errorMsg,
      life: 4000,
    });
  } finally {
    savingTitle.value = false;
  }
}

function isSavingFavorite(workId: string) {
  return savingFavoriteIds.value.has(workId);
}

function setSavingFavorite(workId: string, saving: boolean) {
  const next = new Set(savingFavoriteIds.value);
  if (saving) {
    next.add(workId);
  } else {
    next.delete(workId);
  }
  savingFavoriteIds.value = next;
}

async function toggleFavorite(work: Work) {
  if (isSavingFavorite(work.id)) {
    return;
  }

  const nextValue = !work.is_favorite;
  const previousValue = work.is_favorite;

  work.is_favorite = nextValue;
  setSavingFavorite(work.id, true);

  try {
    await trabalhoService.updateTrabalho(Number(work.id), { is_favorite: nextValue });
  } catch (err: any) {
    work.is_favorite = previousValue;
    const errorMsg = err?.response?.data?.message || "Falha ao atualizar favorito";
    toast.add({
      severity: "error",
      summary: "Erro",
      detail: errorMsg,
      life: 4000,
    });
  } finally {
    setSavingFavorite(work.id, false);
  }
}

function getWorkStatus(work: Work) {
  if (work.defesa_status === 'CONCLUIDA_APROVADO') {
    return {
      label: "Aprovado",
      className: "status-approved",
      iconClass: "fa-solid fa-circle-check",
    };
  }

  if (work.defesa_status === 'CONCLUIDA_REPROVADO') {
    return {
      label: "Reprovado",
      className: "status-rejected",
      iconClass: "fa-solid fa-circle-xmark",
    };
  }

  if (work.defesa_status === 'CONCLUIDA_APROVADO_COM_CORRECOES') {
    return {
      label: "Aprovado com Correções",
      className: "status-corrections",
      iconClass: "fa-solid fa-triangle-exclamation",
    };
  }

  if (work.is_oficial) {
    return {
      label: "Trabalho Academico - Versao Oficial",
      className: "status-official",
      iconClass: "fa-solid fa-user-graduate",
    };
  }

  return {
    label: "",
    className: "",
    iconClass: "",
  };
}

function abrirPeriodoDialog(work: Work) {
  periodoDialogWork.value = work;
  periodoDialogVisible.value = true;
}

return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(GlobalHeader, {
      title: pageTitle.value,
      description: pageDescription.value
    }, null, 8, ["title", "description"]),
    (!_unref(cursoStore).loading)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          (_unref(can)('trabalho.create'))
            ? (_openBlock(), _createBlock(_unref(Button), {
                key: 0,
                label: "Novo Trabalho",
                icon: "pi pi-plus",
                onClick: criarNovoTrabalho,
                loading: criandoTrabalho.value,
                class: "btn-novo-trabalho"
              }, null, 8, ["loading"]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    (_unref(cursoStore).loading)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(_unref(ProgressSpinner)),
          _cache[3] || (_cache[3] = _createElementVNode("p", { class: "m-0 text-base text-500" }, "Carregando curso...", -1))
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_4, [
          (!cursoAtual.value && _unref(cursoStore).hasCursos)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, _cache[4] || (_cache[4] = [
                _createElementVNode("i", { class: "pi pi-info-circle" }, null, -1),
                _createElementVNode("span", null, [
                  _createTextVNode(" Exibindo "),
                  _createElementVNode("strong", null, "todos os seus trabalhos"),
                  _createTextVNode(". Selecione um curso no menu superior para filtrar. ")
                ], -1)
              ])))
            : _createCommentVNode("", true),
          (loadingWorks.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                _createVNode(_unref(ProgressSpinner)),
                _cache[5] || (_cache[5] = _createElementVNode("p", { class: "m-0" }, "Carregando trabalhos...", -1))
              ]))
            : (filteredWorks.value.length === 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                  _cache[6] || (_cache[6] = _createElementVNode("i", { class: "pi pi-folder-open text-4xl mb-2 block" }, null, -1)),
                  _cache[7] || (_cache[7] = _createElementVNode("p", { class: "m-0" }, "Voce ainda nao possui trabalhos.", -1)),
                  (_unref(can)('trabalho.create'))
                    ? (_openBlock(), _createElementBlock("small", _hoisted_8, " Clique em \"Novo Trabalho\" para comecar. "))
                    : _createCommentVNode("", true)
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_9, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(workSections.value, (section) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: section.key,
                      class: "work-section"
                    }, [
                      (section.showTitle)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                            _createElementVNode("span", _hoisted_11, _toDisplayString(section.title), 1),
                            _cache[8] || (_cache[8] = _createElementVNode("span", {
                              class: "section-divider",
                              "aria-hidden": "true"
                            }, null, -1))
                          ]))
                        : _createCommentVNode("", true),
                      _createElementVNode("div", _hoisted_12, [
                        (section.items.length === 0)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_13, _cache[9] || (_cache[9] = [
                              _createElementVNode("p", { class: "text-500 text-sm m-0 py-1" }, "Nenhum trabalho favorito ainda.", -1)
                            ])))
                          : _createCommentVNode("", true),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(section.items, (work) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: work.id,
                            class: "col-12 md:col-5 xl:col-4"
                          }, [
                            _createVNode(_unref(Card), { class: "work-card" }, {
                              title: _withCtx(() => [
                                _createElementVNode("div", _hoisted_14, [
                                  (getWorkStatus(work).label)
                                    ? (_openBlock(), _createElementBlock("span", {
                                        key: 0,
                                        class: _normalizeClass(["status-pill", getWorkStatus(work).className])
                                      }, [
                                        (getWorkStatus(work).iconClass)
                                          ? (_openBlock(), _createElementBlock("i", {
                                              key: 0,
                                              class: _normalizeClass([getWorkStatus(work).iconClass, "mr-2"]),
                                              "aria-hidden": "true"
                                            }, null, 2))
                                          : _createCommentVNode("", true),
                                        _createTextVNode(" " + _toDisplayString(getWorkStatus(work).label), 1)
                                      ], 2))
                                    : _createCommentVNode("", true),
                                  (!isFinalized(work))
                                    ? (_openBlock(), _createElementBlock("button", {
                                        key: 1,
                                        class: _normalizeClass(["favorite-btn", { 'is-active': work.is_favorite }]),
                                        type: "button",
                                        disabled: isSavingFavorite(work.id),
                                        "aria-label": work.is_favorite ? 'Remover dos favoritos' : 'Favoritar',
                                        "aria-pressed": work.is_favorite,
                                        onClick: _withModifiers(($event: any) => (toggleFavorite(work)), ["stop"])
                                      }, [
                                        _createElementVNode("i", {
                                          class: _normalizeClass(work.is_favorite ? 'fa-solid fa-star' : 'fa-regular fa-star'),
                                          "aria-hidden": "true"
                                        }, null, 2)
                                      ], 10, _hoisted_15))
                                    : _createCommentVNode("", true)
                                ]),
                                _createElementVNode("div", _hoisted_16, [
                                  (editingWorkId.value === work.id)
                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                        _createVNode(_unref(InputText), {
                                          modelValue: editingTitle.value,
                                          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((editingTitle).value = $event)),
                                          class: "title-input",
                                          onKeyup: [
                                            _withKeys(($event: any) => (saveTitle(work)), ["enter"]),
                                            _withKeys(cancelEdit, ["escape"])
                                          ],
                                          autofocus: ""
                                        }, null, 8, ["modelValue", "onKeyup"]),
                                        _withDirectives(_createVNode(_unref(Button), {
                                          icon: "fa-solid fa-check",
                                          variant: "text",
                                          severity: "success",
                                          size: "small",
                                          loading: savingTitle.value,
                                          onClick: ($event: any) => (saveTitle(work))
                                        }, null, 8, ["loading", "onClick"]), [
                                          [
                                            _directive_tooltip,
                                            'Salvar',
                                            void 0,
                                            { top: true }
                                          ]
                                        ]),
                                        _withDirectives(_createVNode(_unref(Button), {
                                          icon: "fa-solid fa-xmark",
                                          variant: "text",
                                          severity: "secondary",
                                          size: "small",
                                          onClick: cancelEdit,
                                          disabled: savingTitle.value
                                        }, null, 8, ["disabled"]), [
                                          [
                                            _directive_tooltip,
                                            'Cancelar',
                                            void 0,
                                            { top: true }
                                          ]
                                        ])
                                      ], 64))
                                    : (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(work.title), 1))
                                ])
                              ]),
                              content: _withCtx(() => [
                                _createElementVNode("div", _hoisted_18, [
                                  _createTextVNode(" Criado em " + _toDisplayString(work.createdAt), 1),
                                  _cache[10] || (_cache[10] = _createElementVNode("br", null, null, -1)),
                                  _createTextVNode(" Atualizado em " + _toDisplayString(work.updatedAt), 1)
                                ])
                              ]),
                              footer: _withCtx(() => [
                                _createElementVNode("div", _hoisted_19, [
                                  _createElementVNode("div", _hoisted_20, [
                                    (_unref(can)('trabalho.update'))
                                      ? _withDirectives((_openBlock(), _createBlock(_unref(Button), {
                                          key: 0,
                                          icon: "fa-solid fa-pen",
                                          variant: "text",
                                          size: "small",
                                          class: "icon-btn",
                                          onClick: ($event: any) => (startEdit(work))
                                        }, null, 8, ["onClick"])), [
                                          [
                                            _directive_tooltip,
                                            'Editar titulo',
                                            void 0,
                                            { top: true }
                                          ]
                                        ])
                                      : _createCommentVNode("", true),
                                    (podeExcluirTrabalho(work))
                                      ? (_openBlock(), _createBlock(_unref(Button), {
                                          key: 1,
                                          icon: "fa-solid fa-trash",
                                          variant: "text",
                                          size: "small",
                                          class: "icon-btn icon-btn--danger",
                                          onClick: ($event: any) => (confirmDelete(work))
                                        }, null, 8, ["onClick"]))
                                      : _createCommentVNode("", true),
                                    (work.is_oficial && work.data_fim)
                                      ? _withDirectives((_openBlock(), _createBlock(_unref(Button), {
                                          key: 2,
                                          icon: "pi pi-calendar",
                                          variant: "text",
                                          size: "small",
                                          class: "icon-btn",
                                          onClick: ($event: any) => (abrirPeriodoDialog(work))
                                        }, null, 8, ["onClick"])), [
                                          [
                                            _directive_tooltip,
                                            'Ver Período',
                                            void 0,
                                            { top: true }
                                          ]
                                        ])
                                      : _createCommentVNode("", true)
                                  ]),
                                  (_unref(can)('trabalho.view'))
                                    ? (_openBlock(), _createBlock(_unref(Button), {
                                        key: 0,
                                        icon: "fa-solid fa-folder-open",
                                        label: "Abrir",
                                        size: "small",
                                        class: "btn-abrir",
                                        onClick: ($event: any) => (openEditor(work))
                                      }, null, 8, ["onClick"]))
                                    : _createCommentVNode("", true)
                                ])
                              ]),
                              _: 2
                            }, 1024)
                          ]))
                        }), 128))
                      ])
                    ]))
                  }), 128))
                ]))
        ])),
    _createVNode(PeriodoDialog, {
      visible: periodoDialogVisible.value,
      "onUpdate:visible": _cache[1] || (_cache[1] = ($event: any) => ((periodoDialogVisible).value = $event)),
      titulo: periodoDialogWork.value?.title ? `Período - ${periodoDialogWork.value.title}` : 'Período do TCC',
      "data-inicio": periodoDialogWork.value?.data_inicio,
      "data-fim": periodoDialogWork.value?.data_fim,
      "status-prazo": periodoDialogWork.value?.status_prazo
    }, null, 8, ["visible", "titulo", "data-inicio", "data-fim", "status-prazo"]),
    _createVNode(TrabalhoEmUsoDialog, {
      visible: _unref(trabalhoEmUso).dialogVisivel.value,
      "onUpdate:visible": _cache[2] || (_cache[2] = ($event: any) => ((_unref(trabalhoEmUso).dialogVisivel.value) = $event)),
      "editando-por": _unref(trabalhoEmUso).editandoPor.value,
      "antes-de-abrir": "",
      onAbrir: _unref(trabalhoEmUso).abrirMesmoAssim
    }, null, 8, ["visible", "editando-por", "onAbrir"])
  ]))
}
}

})